import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download } from 'lucide-react';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort, formatOsloDate, formatRange, hoursBetween, osloDateOf, osloTimeOf } from '@/domain/time';
import { COVERAGE_LABELS, DAY_KIND_LABELS } from '@/lib/labels';
import { machineLabel, presentText } from '@/lib/present';
import { exportDaysCsv } from '@/lib/exports';
import { KpiStrip, KpiStripCell } from '@/components/app/Kpi';
import { MetricExplainDialog } from '@/components/app/MetricExplainDialog';
import { FractionChart, ShiftTimeline, TimelineLegend, CHART } from '@/components/app/Charts';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { Note, SourceStatusBadge } from '@/components/app/Badges';
import { PageHeader } from '@/components/app/PageHeader';
import { ProvenanceList } from '@/components/app/RecordViewer';
import { cn } from '@/lib/utils';

export default function ProduksjonPage() {
  const r = useResults();
  const { role } = useSession();
  const { sources, dataset } = useData();
  const [explainKey, setExplainKey] = useState(null);
  const [segment, setSegment] = useState(null);

  const segmentDetails = useMemo(() => {
    if (!segment || !dataset) return null;
    const prods = dataset.production.filter((p) => p.fromUtc === segment.fromUtc && p.toUtc === segment.toUtc);
    const stops = dataset.lineStates.filter((s) => s.state === 'stop' && s.fromUtc < segment.toUtc && s.toUtc > segment.fromUtc && s.id !== segment.id);
    const devs = r.deviations.filter((d) => d.scope.machineId && d.period.fromUtc <= segment.fromUtc && d.period.toUtc >= segment.toUtc);
    return { prods, stops, devs };
  }, [segment, dataset, r.deviations]);

  if (!r.ready) return <PageSkeleton />;
  const { current: c, comparison: cmp } = r;
  const smartdok = sources.find((s) => s.sourceId === 'smartdok');
  const days = c.days;
  const showTimelines = days.length <= 10;
  const plannedOff = c.tonnes.reason === 'planned_off';
  const noData = c.tonnes.reason === 'no_data';
  const totalByFraction = c.tonnesByFraction;
  const total = Object.values(totalByFraction).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      <PageHeader title="Produksjon" lead={`${formatRange(r.fromDate, r.toDate)} · produsert mengde og linjetid fra knuseverket`}>
        <Select value={r.fractionId ?? 'alle'} onValueChange={(v) => r.set({ fraksjon: v === 'alle' ? null : v })}>
          <SelectTrigger id="fraction-filter" className="h-9 w-[200px] rounded-[10px] bg-white text-[14px]" data-testid="fraction-filter-select" aria-label="Fraksjon">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle fraksjoner</SelectItem>
            {dataset.fractions.map((f) => (
              <SelectItem key={f.fractionId} value={f.fractionId} data-testid={`fraction-option-${f.fractionId}`}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => exportDaysCsv(r, role)} data-testid="export-days-csv-button">
          <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> CSV
        </Button>
      </PageHeader>

      {plannedOff && <EmptyState type="planned_off" title="Ingen planlagt produksjon" description={`${formatRange(r.fromDate, r.toDate)} er planlagt driftsfri.`} onReset={r.reset} />}
      {noData && <EmptyState type="no_results" title="Ingen produksjonsdata" description="Perioden ligger utenfor datavinduet (07.07.2026 – 04.10.2026)." onReset={r.reset} />}
      {c.fractionFilterActive && (
        <Note tone="amber" data-testid="fraction-filter-note">
          Fraksjonsfilter aktivt: {fmtNumber(c.tonnes.value, 0)} t for valgt fraksjon. Forholdstall per tonn er slått av fordi maskinforbruket ikke er fordelt per fraksjon.
        </Note>
      )}

      <KpiStrip data-testid="production-kpi-row">
        <KpiStripCell metric={c.tonnes} row={cmp.byKey.tonnes} invertGood onExplain={() => setExplainKey('tonnes')} testKey="prod-tonnes" spark={days.length > 2 ? days.map((d) => (d.kind === 'planned_off' ? null : d.tonnes)) : undefined} sparkColor={CHART.bar} sparkLabel="Produsert per dag" />
        <KpiStripCell metric={c.productionHours} row={cmp.byKey.productionHours} invertGood onExplain={() => setExplainKey('productionHours')} testKey="prod-hours" />
        <KpiStripCell metric={c.stopHours} row={cmp.byKey.stopHours} onExplain={() => setExplainKey('stopHours')} testKey="prod-stop-hours" />
        <KpiStripCell metric={c.productionRate} row={cmp.byKey.productionRate} invertGood onExplain={() => setExplainKey('productionRate')} testKey="prod-rate" />
      </KpiStrip>

      {showTimelines && !plannedOff && !noData && (
        <Panel data-testid="shift-timeline-panel">
          <PanelHeader title="Skifttidslinje" description="Klikk en produksjonsperiode eller et stopp for detaljer." />
          <div className="space-y-5 px-5 py-4">
            {days.map((d) => (
              <div key={d.date} className="space-y-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="hr-tabular font-medium text-[var(--hr-heading)]">{formatDateShort(d.date)}</span>
                  <span className="hr-tabular text-[13px] text-[var(--hr-muted)]">
                    {DAY_KIND_LABELS[d.kind]} · {d.kind === 'planned_off' ? 'Ingen planlagt produksjon' : `${fmtNumber(d.tonnes, 0)} t · produksjon ${fmtNumber(d.productionHours, 1)} h · stopp ${fmtNumber(d.stopHours, 1)} h`}
                  </span>
                </div>
                <ShiftTimeline date={d.date} lineStates={c.lineStates.filter((s) => osloDateOf(s.fromUtc) === d.date)} onSegmentClick={setSegment} compact={days.indexOf(d) !== 0} />
              </div>
            ))}
            <TimelineLegend />
          </div>
        </Panel>
      )}
      {!showTimelines && <Note>Tidslinjen vises for perioder på inntil 10 dager. Klikk en dag i tabellen for å se den.</Note>}

      <div className="grid gap-5 lg:grid-cols-12">
        <Panel className="min-w-0 lg:col-span-7" data-testid="day-table-panel">
          <PanelHeader title="Per dag" description="Klikk en rad for å filtrere til dagen." />
          <div className="hr-scroll overflow-x-auto">
            <Table data-testid="day-table" className="[&_td]:py-3 [&_th]:h-10">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Dato</TableHead>
                  <TableHead className="hidden md:table-cell">Dagtype</TableHead>
                  <TableHead className="text-right">Produsert (t)</TableHead>
                  <TableHead className="hidden min-w-[140px] sm:table-cell">Fraksjoner</TableHead>
                  <TableHead className="text-right">Prod. (h)</TableHead>
                  <TableHead className="text-right">Stopp (h)</TableHead>
                  <TableHead className="hidden text-right md:table-cell">t/h</TableHead>
                  <TableHead className="pr-5">Dekning</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {days.map((d) => (
                  <TableRow key={d.date} className="cursor-pointer" onClick={() => r.setPeriod(d.date, d.date)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && r.setPeriod(d.date, d.date)} data-testid={`day-table-row-${d.date}`}>
                    <TableCell className="hr-tabular pl-5 font-medium">{formatDateShort(d.date)}</TableCell>
                    <TableCell className="hidden text-[var(--hr-muted)] md:table-cell">{DAY_KIND_LABELS[d.kind]}</TableCell>
                    <TableCell className="hr-num text-right font-semibold text-[var(--hr-heading)]">{d.kind === 'planned_off' ? <span className="font-sans text-[13px] font-normal text-[var(--hr-muted)]">Ingen planlagt produksjon</span> : fmtNumber(d.tonnes, 0)}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {d.kind !== 'planned_off' && Object.keys(d.tonnesByFraction).length > 0 && (
                        <div className="flex h-2 w-full overflow-hidden rounded-full bg-[var(--hr-inset)]" aria-label={dataset.fractions.map((f) => `${f.name}: ${fmtNumber(d.tonnesByFraction[f.fractionId] ?? 0, 0)} t`).join(', ')} title={dataset.fractions.map((f) => `${f.name}: ${fmtNumber(d.tonnesByFraction[f.fractionId] ?? 0, 0)} t`).join(' · ')}>
                          {dataset.fractions.map((f, i) => {
                            const sum = Object.values(d.tonnesByFraction).reduce((a, b) => a + b, 0);
                            const w = sum > 0 ? ((d.tonnesByFraction[f.fractionId] ?? 0) / sum) * 100 : 0;
                            return <span key={f.fractionId} style={{ width: `${w}%`, background: i === 0 ? CHART.f1 : CHART.f2 }} />;
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hr-num text-right">{fmtNumber(d.productionHours, 1)}</TableCell>
                    <TableCell className="hr-num text-right">{fmtNumber(d.stopHours, 1)}</TableCell>
                    <TableCell className="hr-num hidden text-right md:table-cell">{d.productionRate === null ? '–' : fmtNumber(d.productionRate, 0)}</TableCell>
                    <TableCell className={cn('pr-5 text-[13px]', d.coverage === 'partial' ? 'font-medium text-[var(--hr-amber)]' : 'text-[var(--hr-muted)]')}>{COVERAGE_LABELS[d.coverage]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {total > 0 && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-[var(--hr-hairline)] px-5 py-3 text-sm">
              <span className="hr-tabular font-semibold text-[var(--hr-heading)]">Sum {fmtNumber(total, 0)} t</span>
              {dataset.fractions.map((f, i) => (
                <span key={f.fractionId} className="hr-tabular inline-flex items-center gap-1.5 text-[var(--hr-muted)]">
                  <span className="inline-block h-2 w-2 rounded-[2px]" style={{ background: i === 0 ? CHART.f1 : CHART.f2 }} aria-hidden="true" />
                  {f.name}: {fmtNumber(totalByFraction[f.fractionId] ?? 0, 0)} t
                </span>
              ))}
            </div>
          )}
        </Panel>

        <div className="min-w-0 space-y-5 lg:col-span-5">
          {!plannedOff && !noData && <FractionChart days={days.filter((d) => d.kind !== 'planned_off')} fractions={dataset.fractions} />}
          <Panel data-testid="context-loads-panel">
            <PanelHeader
              title="Lastet mengde (SmartDok)"
              description="Kontekst – legges aldri til produksjonssummen."
              actions={smartdok && <SourceStatusBadge status={smartdok.status} />}
            />
            <div className="grid gap-5 px-5 py-4 sm:grid-cols-2">
              <div>
                <div className="text-[14px] font-medium text-[var(--hr-muted)]">Lastet til båt</div>
                <div className="hr-num mt-1 text-[26px] font-semibold leading-none text-[var(--hr-heading)]" data-testid="context-boat-tonnes">{fmtNumber(c.contextLoads.båt, 0)} <span className="text-sm font-medium text-[var(--hr-muted)]">t</span></div>
              </div>
              <div>
                <div className="text-[14px] font-medium text-[var(--hr-muted)]">Lastet til bil</div>
                <div className="hr-num mt-1 text-[26px] font-semibold leading-none text-[var(--hr-heading)]" data-testid="context-truck-tonnes">{fmtNumber(c.contextLoads.bil, 0)} <span className="text-sm font-medium text-[var(--hr-muted)]">t</span></div>
              </div>
              <div className="text-[13px] leading-5 text-[var(--hr-muted)] sm:col-span-2">
                {smartdok?.modeLabel} · {c.contextLoads.recordIds.length} kildeposter
                <div className="mt-2">
                  <ProvenanceList recordIds={c.contextLoads.recordIds} limit={3} label="Poster" testId="context-provenance" />
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </div>

      <MetricExplainDialog metric={explainKey ? c[explainKey] : null} result={c} open={!!explainKey} onClose={() => setExplainKey(null)} />

      <Dialog open={!!segment} onOpenChange={(o) => !o && setSegment(null)}>
        <DialogContent className="max-w-lg" data-testid="segment-dialog">
          {segment && segmentDetails && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{segment.state === 'stop' ? 'Stopp' : 'Produksjonsperiode'} {formatOsloDate(segment.fromUtc)} {osloTimeOf(segment.fromUtc)}–{osloTimeOf(segment.toUtc)}</DialogTitle>
                <DialogDescription>
                  {fmtNumber(hoursBetween(segment.fromUtc, segment.toUtc), 2)} h · Kildepost {presentText(segment.sourceRecordId)} · UTC [{segment.fromUtc}, {segment.toUtc})
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                {segment.stopReason && (
                  <div className="hr-inset px-4 py-3">
                    <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-muted-2)]">Registrert stoppårsak</div>
                    <div className="mt-0.5 font-medium">{segment.stopReason}</div>
                  </div>
                )}
                {segmentDetails.prods.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--hr-hairline)] text-left text-[13px] text-[var(--hr-muted)]">
                        <th className="py-1.5 font-medium">Fraksjon</th>
                        <th className="py-1.5 text-right font-medium">Mengde (t)</th>
                        <th className="py-1.5 text-right font-medium">Kildepost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {segmentDetails.prods.map((p) => (
                        <tr key={p.id} className="border-b border-[var(--hr-hairline)] last:border-0">
                          <td className="py-1.5">{dataset.fractions.find((f) => f.fractionId === p.fractionId)?.name}</td>
                          <td className="hr-num py-1.5 text-right">{fmtNumber(p.producedTonnes, 1)}</td>
                          <td className="py-1.5 text-right font-mono text-[13px]">{presentText(p.sourceRecordId)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-1.5 font-medium">Sum</td>
                        <td className="hr-num py-1.5 text-right font-semibold">{fmtNumber(segmentDetails.prods.reduce((a, p) => a + (p.producedTonnes ?? 0), 0), 1)}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                )}
                <ProvenanceList recordIds={[segment.id, ...segmentDetails.prods.map((p) => p.id)]} label="Kildeposter" testId="segment-provenance" />
                {segmentDetails.devs.length > 0 && (
                  <div>
                    <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-muted-2)]">Avvik i samme periode</div>
                    <ul className="mt-1 list-inside list-disc">
                      {segmentDetails.devs.slice(0, 4).map((d) => (
                        <li key={d.id}>
                          <Button asChild variant="link" className="h-auto p-0 text-sm">
                            <a href={r.linkTo(`/avvik/${encodeURIComponent(d.id)}`)}>{d.ruleLabel}{d.scope.machineId ? ` – ${machineLabel(d.scope.machineId)}` : ''}</a>
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSegment(null)} data-testid="segment-dialog-close">Lukk</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
